import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { t } from '../../utils/translations';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const navigationSections = [
    {
      id: 'overview',
      label: t('nav.overview'),
      items: [
        {
          id: 'dashboard',
          label: t('nav.dashboard'),
          path: '/dashboard',
          icon: 'LayoutDashboard',
          description: t('sidebar.mainOverview')
        }
      ]
    },
    {
      id: 'content',
      label: t('nav.contentManagement'),
      items: [
        {
          id: 'articles',
          label: t('nav.articles'),
          path: '/articles-management',
          icon: 'FileText',
          description: t('sidebar.manageArticles')
        },
        {
          id: 'article-editor',
          label: t('nav.articleEditor'),
          path: '/article-editor',
          icon: 'Edit3',
          description: t('sidebar.createAndEditArticles')
        },
        {
          id: 'projects',
          label: t('nav.projects'),
          path: '/projects-management',
          icon: 'FolderOpen',
          description: t('sidebar.manageUnionProjects')
        },
        {
          id: 'project-form',
          label: t('nav.projectForm'),
          path: '/project-form',
          icon: 'Plus',
          description: t('sidebar.createNewProjects')
        }
      ]
    },
    {
      id: 'organization',
      label: t('nav.organization'),
      items: [
        {
          id: 'cities',
          label: t('nav.cities'),
          path: '/cities-management',
          icon: 'MapPin',
          description: t('sidebar.manageCityLocations')
        },
        {
          id: 'sub-unions',
          label: t('nav.subUnions'),
          path: '/sub-unions-management',
          icon: 'Building2',
          description: t('sidebar.manageSubUnionBranches')
        },
        {
          id: 'sub-union-form',
          label: t('nav.subUnionForm'),
          path: '/sub-union-form',
          icon: 'Plus',
          description: t('sidebar.createNewSubUnions')
        },
        {
          id: 'offices',
          label: t('nav.offices'),
          path: '/offices-management',
          icon: 'Briefcase',
          description: t('sidebar.manageOfficePositions')
        },
        {
          id: 'office-form',
          label: t('nav.officeForm'),
          path: '/office-form',
          icon: 'Plus',
          description: t('sidebar.createNewOfficePositions')
        }
      ]
    }
  ];

  const handleNavigation = (path, itemId) => {
    setActiveSection(itemId);
    window.location.href = path;
  };

  const getCurrentPath = () => {
    return window.location?.pathname;
  };

  const isItemActive = (path) => {
    return getCurrentPath() === path;
  };

  return (
    <aside className={`fixed left-0 top-16 bottom-0 z-100 bg-card border-r border-border transition-all duration-300 ${isCollapsed ? 'w-16 lg:w-16' : 'w-60 lg:w-60'
      } lg:translate-x-0`}>
      <div className="flex flex-col h-full">
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          {!isCollapsed && (
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Users" size={18} color="white" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-foreground font-arabic">
                  {t('sidebar.adminPanel')}
                </h2>
                <p className="text-xs text-muted-foreground font-caption">
                  {t('sidebar.syrianStudentUnion')}
                </p>
              </div>
            </div>
          )}

          {onToggleCollapse && (
            <Button
              variant="ghost"
              size="icon"
              className="hidden lg:flex text-muted-foreground hover:text-foreground"
              onClick={onToggleCollapse}
              iconName={isCollapsed ? 'ChevronRight' : 'ChevronLeft'}
              iconSize={16}
            >
              <span className="sr-only">Toggle sidebar</span>
            </Button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-6">
          {navigationSections?.map((section) => (
            <div key={section?.id} className="space-y-2">
              {!isCollapsed && (
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider font-caption">
                  {section?.label}
                </h3>
              )}

              <div className="space-y-1">
                {section?.items?.map((item) => {
                  const isActive = isItemActive(item?.path);

                  return (
                    <button
                      key={item?.id}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 group ${isActive
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }`}
                      onClick={() => handleNavigation(item?.path, item?.id)}
                      title={isCollapsed ? item?.label : item?.description}
                    >
                      <Icon
                        name={item?.icon}
                        size={18}
                        className={`flex-shrink-0 ${isActive ? 'text-primary-foreground' : 'text-current'
                          }`}
                      />
                      {!isCollapsed && (
                        <div className="flex-1 text-left">
                          <span className="font-medium">{item?.label}</span>
                          {item?.description && (
                            <p className="text-xs opacity-75 mt-0.5">
                              {item?.description}
                            </p>
                          )}
                        </div>
                      )}
                      {!isCollapsed && isActive && (
                        <div className="w-2 h-2 bg-primary-foreground rounded-full flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* User Profile & Logout */}
        <div className="p-4 border-t border-border bg-card">
          {!isCollapsed ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-3 px-1">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shadow-sm">
                  {user?.email?.[0]?.toUpperCase() || 'A'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">
                    {user?.email || 'Admin'}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {t('profile.administrator')}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 border-red-200 dark:border-red-900/50"
                onClick={handleSignOut}
                iconName="LogOut"
                iconSize={16}
              >
                {t('nav.signOut')}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-4">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs shadow-sm">
                {user?.email?.[0]?.toUpperCase() || 'A'}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                onClick={handleSignOut}
                iconName="LogOut"
                iconSize={18}
                title={t('nav.signOut')}
              />
            </div>
          )}
        </div>

      </div>
    </aside>
  );
};

export default Sidebar;